'use client';

import { useRouter, usePathname } from 'next/navigation';
import { LanguageSelectorUI, type Language } from '@/components/ui/language-selector-ui';

type LanguageSelectorProps = {
  languages: Language[];
  currentLang: string;
  translations?: Record<string, string>; // Maps lang -> slug for single posts
  maxPages?: {
    en: number;
    ru: number;
  }; // Optional pagination limits for language switching
};

export default function LanguageSelector({
  languages,
  currentLang,
  maxPages,
  translations,
}: LanguageSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();

  const handleLangChange = (newLang: string) => {
    // Determine the appropriate route based on current path and target language

    // If we're on a single post page
    if (pathname.includes('/posts/') && !pathname.endsWith('/posts') && !pathname.endsWith('/posts/ru')) {
      if (translations && translations[newLang]) {
        // Translation exists: navigate to translated post
        if (newLang === 'en') {
          router.push(`/posts/${translations[newLang]}`);
        } else {
          router.push(`/posts/${newLang}/${translations[newLang]}`);
        }
      } else {
        // No translation exists: redirect to main posts page
        if (newLang === 'en') {
          router.push('/posts');
        } else {
          router.push('/posts/ru');
        }
      }
      return;
    }

    // Helper function to check if a page number exists in target language
    const getMaxPageForLang = (lang: string) => {
      if (!maxPages) return Infinity; // If no limits provided, allow all pages
      return lang === 'en' ? maxPages.en : maxPages.ru;
    };

    // For all other pages (posts index, categories, pagination)
    if (pathname.startsWith('/posts/ru')) {
      // Currently on Russian route
      if (newLang === 'en') {
        // Switch to English: remove /ru from path
        const englishPath = pathname.replace('/posts/ru', '/posts');

        // Check if it's a pagination page
        const pageMatch = pathname.match(/\/posts\/ru\/page\/(\d+)/);
        if (pageMatch) {
          const pageNumber = parseInt(pageMatch[1]);
          const maxEnglishPages = getMaxPageForLang('en');

          // If the page doesn't exist in English, go to main posts page
          if (pageNumber > maxEnglishPages) {
            router.push('/posts');
            return;
          }
        }

        router.push(englishPath === '/posts/' ? '/posts' : englishPath);
      } else if (newLang === 'ru') {
        // Already on Russian, no change needed
        return;
      }
    } else if (pathname.startsWith('/posts')) {
      // Currently on English route
      if (newLang === 'ru') {
        // Switch to Russian: add /ru to path
        const russianPath = pathname.replace('/posts', '/posts/ru');

        // Check if it's a pagination page
        const pageMatch = pathname.match(/\/posts\/page\/(\d+)/);
        if (pageMatch) {
          const pageNumber = parseInt(pageMatch[1]);
          const maxRussianPages = getMaxPageForLang('ru');

          // If the page doesn't exist in Russian, go to main Russian posts page
          if (pageNumber > maxRussianPages) {
            router.push('/posts/ru');
            return;
          }
        }

        router.push(russianPath);
      } else if (newLang === 'en') {
        // Already on English, no change needed
        return;
      }
    }
  };

  return (
    <LanguageSelectorUI
      currentLang={currentLang}
      languages={languages}
      onValueChange={handleLangChange}
    />
  );
}